import {
  apiErrorMessage,
  useClassCompanies,
  useDeleteCompanyClass,
  useRenameCompanyClass,
} from "@vidario/api-client";
import {
  classNameError,
  classSizeLabel,
  type CompanyClassSummary,
  type EmployerGroup,
} from "@vidario/domain";
import { Button, ConfirmDialog, EmptyState, Input, Spinner, useToast } from "@vidario/ui";
import { ChevronLeft, Layers, Pencil, Plus, Trash2 } from "lucide-react";
import { useRef, useState, type ReactElement } from "react";
import { useSearchParams } from "react-router";

import { ClassPicker } from "./ClassPicker";
import { NewClassDialog } from "./dialogs";
import { CompanyName } from "./Spellings";
import { assignedMessage, useAssignWithUndo } from "./useAssignWithUndo";

interface ClassesTabProps {
  classes: readonly CompanyClassSummary[];
  announce: (text: string) => void;
}

export function SizeBadge({ summary }: { summary: CompanyClassSummary }): ReactElement | null {
  const label = classSizeLabel(summary);
  if (!label) return null;
  return (
    <span className="inline-flex shrink-0 items-center rounded-full bg-warning-50 px-2 py-0.5 text-[11px] font-semibold text-warning-700">
      {label}
    </span>
  );
}

/** „Klassen": the classes on the left, the picked class's companies on the
 * right; on phones the list and the class are separate steps. */
export function ClassesTab({ classes, announce }: ClassesTabProps): ReactElement {
  const [params, setParams] = useSearchParams();
  const selectedId = Number(params.get("klasse")) || null;
  const selected = classes.find((c) => c.id === selectedId) ?? null;
  const [newOpen, setNewOpen] = useState(false);
  const remove = useDeleteCompanyClass();
  const { push } = useToast();

  const open = (id: number | null): void => {
    setParams((current) => {
      const next = new URLSearchParams(current);
      if (id === null) next.delete("klasse");
      else next.set("klasse", String(id));
      return next;
    });
  };

  // Leave the class first, so nothing on screen still asks for its companies.
  const listRef = useRef<HTMLUListElement>(null);
  const deleteClass = (doomed: CompanyClassSummary): void => {
    open(null);
    remove.mutate(doomed.id, {
      onSuccess: () => {
        push({ kind: "success", message: `Klasse „${doomed.name}“ gelöscht` });
        announce(`Klasse „${doomed.name}“ gelöscht.`);
        requestAnimationFrame(() =>
          listRef.current?.querySelector<HTMLButtonElement>("button")?.focus(),
        );
      },
      onError: (err) =>
        push({ kind: "error", title: "Klasse nicht gelöscht", message: apiErrorMessage(err) }),
    });
  };

  return (
    <div className="grid gap-6 md:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]">
      <section aria-label="Klassen" className={selected ? "hidden md:block" : "block"}>
        <Button
          size="sm"
          variant="secondary"
          leftIcon={<Plus size={14} />}
          onClick={() => setNewOpen(true)}
          className="mb-3"
        >
          Neue Klasse
        </Button>
        <ul ref={listRef} className="m-0 flex list-none flex-col gap-1 p-0">
          {/* „Sonstige" first, as in the picker, then the rest alphabetically. */}
          {[...classes]
            .sort((a, b) => Number(b.isFallback) - Number(a.isFallback))
            .map((c) => (
              <li
                key={c.id}
                className={c.isFallback ? "mb-1 border-b border-border-default pb-1" : undefined}
              >
                <button
                  type="button"
                  onClick={() => open(c.id)}
                  aria-current={c.id === selected?.id ? "true" : undefined}
                  className={
                    "flex w-full cursor-pointer flex-col items-start gap-1 rounded-md px-3 py-2 text-left text-sm " +
                    "hover:bg-neutral-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 " +
                    (c.id === selected?.id ? "bg-primary-50 text-primary-700" : "text-text-primary")
                  }
                >
                  <span className="font-medium">{c.name}</span>
                  <span className="flex flex-wrap items-center gap-2 text-xs text-text-tertiary">
                    {c.isFallback ? (
                      "Für Einzelfälle · nie zu klein"
                    ) : (
                      <>
                        {c.companyCount} {c.companyCount === 1 ? "Firma" : "Firmen"}
                        {/* An empty class hides nothing yet, so it needs no warning in the list. */}
                        {c.companyCount > 0 && <SizeBadge summary={c} />}
                      </>
                    )}
                  </span>
                </button>
              </li>
            ))}
        </ul>
      </section>

      <section aria-label="Klasse" className={selected ? "block" : "hidden md:block"}>
        {selected ? (
          <ClassDetail
            key={selected.id}
            summary={selected}
            classes={classes}
            announce={announce}
            onBack={() => open(null)}
            onDelete={() => deleteClass(selected)}
          />
        ) : (
          <EmptyState
            icon={<Layers size={28} />}
            title="Keine Klasse gewählt"
            description="Wähle links eine Klasse, um ihre Firmen zu sehen."
          />
        )}
      </section>

      <NewClassDialog
        open={newOpen}
        onOpenChange={setNewOpen}
        classes={classes}
        onCreated={(created) => open(created.id)}
      />
    </div>
  );
}

interface ClassDetailProps {
  summary: CompanyClassSummary;
  classes: readonly CompanyClassSummary[];
  announce: (text: string) => void;
  onBack: () => void;
  onDelete: () => void;
}

function ClassDetail({
  summary,
  classes,
  announce,
  onBack,
  onDelete,
}: ClassDetailProps): ReactElement {
  const { data: companies, isLoading, error, refetch } = useClassCompanies(summary.id);
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [deleteOpen, setDeleteOpen] = useState(false);
  const { assign, pending } = useAssignWithUndo(announce);

  const rows = companies ?? [];
  const selectedRows = rows.filter((r) => selected.has(r.id));
  const allSelected = rows.length > 0 && rows.every((r) => selected.has(r.id));
  const tableRef = useRef<HTMLTableElement>(null);
  // The bulk bar goes with the selection; keep keyboard focus in the table.
  const done = (): void => {
    setSelected(new Set());
    requestAnimationFrame(() =>
      (
        tableRef.current?.querySelector<HTMLElement>("tbody input[type='checkbox']") ??
        tableRef.current?.querySelector<HTMLElement>("caption")
      )?.focus(),
    );
  };

  const move = (target: readonly EmployerGroup[], classId: number, className: string): void =>
    assign({ rows: target, classId, message: assignedMessage(target, className), onDone: done });
  const unassign = (target: readonly EmployerGroup[]): void =>
    assign({
      rows: target,
      classId: null,
      message:
        target.length === 1
          ? `„${target[0]?.name ?? ""}“ aus der Klasse entfernt`
          : `${target.length} Firmen aus der Klasse entfernt`,
      onDone: done,
    });

  const confirmDelete = (): void => {
    setDeleteOpen(false);
    onDelete();
  };

  return (
    <div>
      <button
        type="button"
        onClick={onBack}
        className="mb-3 inline-flex cursor-pointer items-center gap-1 text-sm text-text-secondary hover:text-text-primary md:hidden"
      >
        <ChevronLeft size={16} /> Alle Klassen
      </button>

      <ClassHeader
        summary={summary}
        classes={classes}
        announce={announce}
        onDelete={() => setDeleteOpen(true)}
      />

      {summary.tooSmall && (
        <p className="mt-1 mb-0 text-xs text-text-secondary">
          Erscheint in der Anonym-Ansicht als „Sonstige“.
        </p>
      )}

      {selectedRows.length > 0 && (
        <div
          role="region"
          aria-label="Auswahl"
          className="mt-4 flex flex-wrap items-center gap-3 rounded-md border border-border-default bg-neutral-50 px-4 py-2.5 text-sm"
        >
          <span className="font-medium">{selectedRows.length} ausgewählt</span>
          <div className="w-56">
            <ClassPicker
              classes={classes}
              currentClassId={summary.id}
              aria-label="Verschieben nach"
              placeholder="Verschieben nach…"
              onPick={(classId, name) => move(selectedRows, classId, name)}
              disabled={pending}
            />
          </div>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => unassign(selectedRows)}
            disabled={pending}
          >
            Aus Klasse entfernen
          </Button>
        </div>
      )}

      <div className="mt-4">
        {isLoading && (
          <div className="flex items-center gap-2 py-6 text-sm text-text-secondary">
            <Spinner size={18} />
            <span>Lädt</span>
          </div>
        )}
        {error && (
          <div role="alert" className="flex items-center gap-3 py-6 text-sm text-danger-600">
            Firmen konnten nicht geladen werden.
            <Button size="sm" variant="secondary" onClick={() => void refetch()}>
              Erneut versuchen
            </Button>
          </div>
        )}
        {companies && rows.length === 0 && (
          <p className="py-6 text-sm text-text-secondary">
            Noch keine Firmen in dieser Klasse. Ordne sie unter „Nicht zugeordnet“ zu.
          </p>
        )}
        {rows.length > 0 && (
          <div className="overflow-x-auto rounded-lg border border-border-default">
            <table ref={tableRef} className="w-full border-collapse text-sm">
              <caption tabIndex={-1} className="sr-only">
                Firmen in „{summary.name}“
              </caption>
              <thead>
                <tr className="bg-neutral-50 text-left text-[11px] font-semibold uppercase tracking-wider text-text-tertiary">
                  <th scope="col" className="w-10 border-b border-border-default px-4 py-3">
                    <input
                      type="checkbox"
                      aria-label="Alle Firmen dieser Klasse auswählen"
                      checked={allSelected}
                      onChange={() =>
                        setSelected(allSelected ? new Set() : new Set(rows.map((r) => r.id)))
                      }
                    />
                  </th>
                  <th scope="col" className="border-b border-border-default px-4 py-3">
                    Firma
                  </th>
                  <th scope="col" className="border-b border-border-default px-4 py-3 text-right">
                    Kandidaten
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id} className="border-b border-border-default last:border-b-0">
                    <td className="px-4 py-2.5">
                      <input
                        type="checkbox"
                        aria-label={`„${row.name}“ auswählen`}
                        checked={selected.has(row.id)}
                        onChange={() =>
                          setSelected((s) => {
                            const next = new Set(s);
                            if (next.has(row.id)) next.delete(row.id);
                            else next.add(row.id);
                            return next;
                          })
                        }
                      />
                    </td>
                    <td className="max-w-80 px-4 py-2.5">
                      <CompanyName group={row} />
                    </td>
                    <td className="tabular-nums px-4 py-2.5 text-right text-text-secondary">
                      {row.candidateCount}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title={`Klasse „${summary.name}“ löschen?`}
        confirmLabel="Klasse löschen"
        // A reflex Enter keeps everything (#186).
        initialFocus="cancel"
        tone="destructive"
        onConfirm={confirmDelete}
      >
        {summary.companyCount === 0
          ? "Die Klasse enthält keine Firmen."
          : `${summary.companyCount === 1 ? "Die Firma dieser Klasse kommt" : `Die ${summary.companyCount} Firmen dieser Klasse kommen`} zurück zu „Nicht zugeordnet“.`}{" "}
        Kandidaten bleiben unverändert.
      </ConfirmDialog>
    </div>
  );
}

function ClassHeader({
  summary,
  classes,
  announce,
  onDelete,
}: {
  summary: CompanyClassSummary;
  classes: readonly CompanyClassSummary[];
  announce: (text: string) => void;
  onDelete: () => void;
}): ReactElement {
  const [editing, setEditing] = useState(false);
  const pencilRef = useRef<HTMLButtonElement>(null);
  // The form goes away on save or Esc; focus returns to the pencil that opened it.
  const stopEditing = (): void => {
    setEditing(false);
    requestAnimationFrame(() => pencilRef.current?.focus());
  };
  const [name, setName] = useState(summary.name);
  const rename = useRenameCompanyClass();
  const { push } = useToast();
  const error = editing ? classNameError(name, classes, summary.id) : null;

  const save = (): void => {
    if (error) return;
    if (name.trim() === summary.name) {
      stopEditing();
      return;
    }
    rename.mutate(
      { id: summary.id, name },
      {
        onSuccess: (renamed) => {
          announce(`Klasse umbenannt in „${renamed.name}“.`);
          stopEditing();
        },
        onError: (err) =>
          push({ kind: "error", title: "Nicht umbenannt", message: apiErrorMessage(err) }),
      },
    );
  };

  if (editing) {
    return (
      <form
        className="flex items-start gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          save();
        }}
      >
        <div className="flex-1">
          <Input
            aria-label="Name der Klasse"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                setName(summary.name);
                stopEditing();
              }
            }}
            error={error}
            ref={(el) => el?.focus()}
          />
        </div>
        <Button type="submit" size="md" loading={rename.isPending}>
          Speichern
        </Button>
      </form>
    );
  }

  return (
    <div className="flex items-start gap-3">
      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-3 gap-y-1">
        <h2 className="m-0 min-w-0 break-words text-xl font-semibold">{summary.name}</h2>
        <SizeBadge summary={summary} />
      </div>
      {!summary.isFallback && (
        <div className="flex shrink-0 gap-1">
          <Button
            ref={pencilRef}
            variant="icon"
            size="sm"
            aria-label="Umbenennen"
            leftIcon={<Pencil size={16} />}
            onClick={() => setEditing(true)}
          />
          <Button
            variant="icon"
            size="sm"
            aria-label="Klasse löschen"
            leftIcon={<Trash2 size={16} />}
            onClick={onDelete}
          />
        </div>
      )}
    </div>
  );
}
